import { ScrollReveal } from '@/components/ui/ScrollReveal';
import { Button } from '@/components/ui/button';
import { LocalizedLink } from '@/components/LocalizedLink';
import { MapPin, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

export const LocationSection = () => {
  const { t } = useTranslation(['home', 'common']);
  // Prime 3 immagini per l'anteprima
  const previewImages = [
    '/img/location/IMG_8110.jpg',
    '/img/location/IMG_8113.jpg',
    '/img/location/IMG_8127.jpg',
  ];

  return (
    <section className="bg-background py-16 md:py-32 px-4 md:px-6" aria-labelledby="location-heading">
      <div className="mx-auto max-w-7xl">
        <div className="grid gap-8 md:gap-16 lg:grid-cols-2 lg:gap-24 items-center">
          {/* Content */}
          <div className="order-2 lg:order-1">
            <ScrollReveal>
              <span className="mb-4 inline-block text-sm font-light uppercase tracking-[0.3em] text-primary">
                {t('home:locationSection.label')}
              </span>
            </ScrollReveal>
            
            <ScrollReveal delay={100}>
              <h2 id="location-heading" className="mb-6 md:mb-8 text-3xl md:text-4xl font-semibold text-foreground md:text-5xl">
                {t('home:locationSection.title')}
                <br />
                <span className="text-primary">{t('home:locationSection.titleOf')}</span>
              </h2>
            </ScrollReveal>
            
            <ScrollReveal delay={200}>
              <p className="mb-4 md:mb-6 text-base md:text-lg font-light text-muted-foreground">
                {t('home:locationSection.text1')}
              </p>
            </ScrollReveal>
            
            <ScrollReveal delay={300}>
              <p className="mb-6 md:mb-10 text-base md:text-lg font-light text-muted-foreground">
                {t('home:locationSection.text2')}
              </p>
            </ScrollReveal>

            <ScrollReveal delay={400}>
              <div className="mb-6 md:mb-8 flex items-start gap-3 md:gap-4 text-muted-foreground">
                <MapPin className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                <span className="text-xs md:text-sm leading-relaxed">{t('home:locationSection.address')}</span>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={500}>
              <Button variant="outline" size="lg" asChild className="w-full md:w-auto">
                <LocalizedLink href="/location" className="group flex items-center justify-center">
                  {t('common:cta.discoverLocation')}
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </LocalizedLink>
              </Button>
            </ScrollReveal>
          </div>

          {/* Image Gallery */}
          <ScrollReveal className="order-1 lg:order-2 w-full">
            <div className="relative w-full max-w-full overflow-hidden rounded-lg">
              <Swiper
                modules={[Navigation, Pagination, Autoplay]}
                spaceBetween={0}
                slidesPerView={1}
                navigation={{
                  prevEl: '.swiper-button-prev-location-preview',
                  nextEl: '.swiper-button-next-location-preview',
                  enabled: false,
                }}
                breakpoints={{
                  768: {
                    navigation: {
                      enabled: true,
                    },
                  },
                }}
                pagination={{
                  clickable: true,
                  dynamicBullets: true,
                }}
                autoplay={{
                  delay: 4000,
                  disableOnInteraction: false,
                }}
                loop={true}
                className="!pb-12 !w-full !max-w-full"
                style={{ width: '100%', maxWidth: '100%' }}
              >
                {previewImages.map((image, index) => (
                  <SwiperSlide key={index} className="!w-full">
                    <div className="relative aspect-[4/3] overflow-hidden w-full">
                      <img
                        src={image}
                        alt={t('home:locationSection.slideAlt', { n: index + 1 })}
                        className="h-full w-full object-cover"
                        loading={index === 0 ? 'eager' : 'lazy'}
                        decoding="async"
                        width="1200"
                        height="900"
                      />
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>

              {/* Custom Navigation Buttons - Hidden on mobile */}
              <button
                type="button"
                className="focus-ring swiper-button-prev-location-preview hidden md:flex absolute left-4 top-1/2 z-10 -translate-y-1/2 h-10 w-10 items-center justify-center rounded-full bg-background/80 backdrop-blur-sm text-foreground transition-all hover:bg-background hover:text-primary"
                aria-label={t('common:aria.prevSlide')}
              >
                <svg
                  className="h-5 w-5"
                  aria-hidden="true"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>
              <button
                type="button"
                className="focus-ring swiper-button-next-location-preview hidden md:flex absolute right-4 top-1/2 z-10 -translate-y-1/2 h-10 w-10 items-center justify-center rounded-full bg-background/80 backdrop-blur-sm text-foreground transition-all hover:bg-background hover:text-primary"
                aria-label={t('common:aria.nextSlide')}
              >
                <svg
                  className="h-5 w-5"
                  aria-hidden="true"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
};

